<script>
import bem from '@/utils/bem';
import { MODULE_TYPES, MODULE_TYPE_LABELS } from '@/constants/pageContent';
import ModuleRenderer from './modules/ModuleRenderer.vue';
import CollectionCardsModule from './modules/templates/CollectionCardsModule.vue';

const b = bem('module-select-modal');

const MODULE_TEMPLATES = {
  [MODULE_TYPES.INFO]: [
    {
      id: 'info-background-right',
      name: 'Background Info Block Right',
      preview: {
        layout: 'background-content',
        contentPosition: 'right',
      },
    },
    {
      id: 'info-background-left',
      name: 'Background Info Block Left',
      preview: {
        layout: 'background-content',
        contentPosition: 'left',
      },
    },
    {
      id: 'info-image-left',
      name: 'Info with Image Left',
      preview: {
        layout: 'image-content',
        imagePosition: 'left',
      },
    },
    {
      id: 'info-image-right',
      name: 'Info with Image Right',
      preview: {
        layout: 'image-content',
        imagePosition: 'right',
      },
    },
    {
      id: 'info-cards',
      name: 'Info Cards Block',
      preview: {
        layout: 'cards',
        cardsCount: 4,
      },
    },
    {
      id: 'info-contact',
      name: 'Contact Info Block',
      preview: {
        layout: 'contact-info',
      },
    },
  ],
  [MODULE_TYPES.PRODUCT]: [
    {
      id: 'product-select',
      name: 'Product Collections',
      description: 'Select collections to display products',
      preview: {
        layout: 'product-select',
      },
    },
  ],
  [MODULE_TYPES.GALLERY]: [
    {
      id: 'gallery-select',
      name: 'Photo Galleries',
      description: 'Select galleries to display photos',
      preview: {
        layout: 'gallery-select',
      },
    },
  ],
  [MODULE_TYPES.CTA]: [
    {
      id: 'cta-background-left',
      name: 'Background CTA Left',
      description: 'Full background image with title, description and button left',
      preview: {
        layout: 'background-cta',
        imagePosition: 'left',
        hasImage: true,
        hasTitle: true,
        hasDescription: true,
        hasButton: true,
      },
    },
    {
      id: 'cta-background-right',
      name: 'Background CTA Right',
      description: 'Full background image with title, description and button right',
      preview: {
        layout: 'background-cta',
        hasImage: true,
        hasTitle: true,
        hasDescription: true,
        hasButton: true,
      },
    },
    {
      id: 'cta-image-left',
      name: 'CTA with Image Left',
      description: 'CTA with image on the left side',
      preview: {
        layout: 'cta-image',
        imagePosition: 'left',
        hasImage: true,
        hasTitle: true,
        hasDescription: true,
        hasButton: true,
      },
    },
    {
      id: 'cta-image-right',
      name: 'CTA with Image Right',
      description: 'CTA with image on the right side',
      preview: {
        layout: 'cta-image',
        imagePosition: 'right',
        hasImage: true,
        hasTitle: true,
        hasDescription: true,
        hasButton: true,
      },
    },
    {
      id: 'cta-ready-order',
      name: 'CTA Ready Order',
      description: 'Two images with title header and action stripes',
      preview: {
        layout: 'cta-ready-order',
        hasImage: true,
        hasTitle: true,
        hasButton: true,
      },
    },
  ],
  [MODULE_TYPES.PRINT_PAL]: [
    {
      id: 'enhance-wizard',
      name: 'Enhance Wizard',
      description: 'Photo enhancement wizard module',
      preview: {
        layout: 'enhance-wizard',
      },
    },
    // TODO: temporarily hidden until ready
    // {
    //   id: 'create-wizard',
    //   name: 'Create Wizard',
    //   description: 'Photo product creation wizard module',
    //   preview: {
    //     layout: 'create-wizard',
    //   },
    // },
  ],
};

export default {
  components: {
    ModuleRenderer,
    CollectionCardsModule,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    moduleType: {
      type: String,
      required: true,
    },
    companyId: {
      type: [String, Number],
      default: null,
    },
    blockIndex: {
      type: Number,
      default: 0,
    },
    companyColors: {
      type: Object,
      default: () => ({
        color1: '',
        color2: '',
        color3: '',
      }),
    },
  },
  emits: ['close', 'select'],
  data() {
    return {
      selectModuleData: null,
    };
  },
  computed: {
    templates() {
      return MODULE_TEMPLATES[this.moduleType] || [];
    },
    moduleTypeLabel() {
      return MODULE_TYPE_LABELS[this.moduleType] || 'Module';
    },
    isSelectTemplate() {
      return this.moduleType === MODULE_TYPES.PRODUCT || this.moduleType === MODULE_TYPES.GALLERY;
    },
    hasFirstBlock() {
      return this.selectModuleData?.selectionCount >= 1;
    },
    hasSecondBlock() {
      return this.selectModuleData?.selectionCount >= 2;
    },
    selectedIds() {
      const parsedContent = this.selectModuleData?.parsedContent;
      if (!parsedContent) return [];
      return parsedContent.collectionIds || parsedContent.galleryIds || [];
    },
    itemType() {
      return this.moduleType === MODULE_TYPES.PRODUCT ? 'collection' : 'gallery';
    },
    cardsModuleData() {
      const templateId = this.moduleType === MODULE_TYPES.PRODUCT ? 'collection-cards' : 'gallery-cards';
      return {
        templateId,
        content: JSON.stringify({ type: this.itemType }),
        parsedContent: { type: this.itemType },
      };
    },
  },
  watch: {
    visible(newVal) {
      if (!newVal) {
        this.selectModuleData = null;
      }
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    handleSelect(template) {
      this.$emit('select', {
        templateId: template.id,
        type: this.moduleType,
        templateName: template.name,
      });
    },
    handleClose() {
      this.$emit('close');
    },
    getPreviewClass(template) {
      const preview = template.preview || {};
      return {
        'image-left': preview.layout === 'image-left',
        'image-right': preview.layout === 'image-right',
        centered: preview.layout === 'centered',
        grid: preview.layout === 'grid',
        list: preview.layout === 'list',
        featured: preview.layout === 'featured',
        slider: preview.layout === 'slider',
        masonry: preview.layout === 'masonry',
        banner: preview.layout === 'banner',
        form: preview.layout === 'form',
        'background-centered': preview.layout === 'background-centered',
        'background-cta': preview.layout === 'background-cta',
        'cta-image': preview.layout === 'cta-image',
        'create-wizard': preview.layout === 'create-wizard',
      };
    },
    getModuleData(template) {
      return {
        templateId: template.id,
        content: JSON.stringify({}),
        parsedContent: {
          templateId: template.id,
        },
      };
    },
    hasRealComponent(template) {
      const realComponents = [
        'info-background-right',
        'info-background-left',
        'info-image-left',
        'info-image-right',
        'info-cards',
        'info-contact',
        'cta-background-left',
        'cta-background-right',
        'cta-image-left',
        'cta-image-right',
        'cta-ready-order',
        'product-select',
        'gallery-select',
        'enhance-wizard',
      ];
      return realComponents.includes(template.id);
    },
    handleSelectionChange(data) {
      this.selectModuleData = data;
    },
    handleAddFirstBlock() {
      if (!this.selectModuleData) return;
      this.$emit('select', {
        templateId: this.selectModuleData.templateId,
        type: this.selectModuleData.type,
        blockType: 'items',
        content: this.selectModuleData.content,
        parsedContent: this.selectModuleData.parsedContent,
      });
    },
    handleAddSecondBlock() {
      if (!this.selectModuleData) return;
      const templateId = this.moduleType === MODULE_TYPES.PRODUCT ? 'collection-cards' : 'gallery-cards';
      const parsedContent = this.selectModuleData.parsedContent || {};

      // Вычисляем фон для cards на основе того, как закончился items блок
      // items чередует фон: startWithColor определяет с чего начать
      // Если startWithColor=true: 0-й с фоном, 1-й без, 2-й с фоном...
      // Если startWithColor=false: 0-й без фона, 1-й с, 2-й без...
      const itemsCount = this.selectedIds.length;
      const { startWithColor } = parsedContent;
      // Индекс последнего элемента items
      const lastItemIndex = itemsCount - 1;
      // Последний элемент items имеет фон?
      const lastItemHasBackground = startWithColor
        ? (lastItemIndex % 2 === 0)
        : (lastItemIndex % 2 === 1);
      // Cards должен чередоваться - если последний items с фоном, cards без фона и наоборот
      const hasBackground = !lastItemHasBackground;

      const content = {
        templateId,
        type: this.itemType,
        collectionIds: parsedContent.collectionIds,
        galleryIds: parsedContent.galleryIds,
        galleries: parsedContent.galleries,
        backgroundColor: hasBackground ? parsedContent.backgroundColor : null,
      };
      this.$emit('select', {
        templateId,
        type: this.selectModuleData.type,
        blockType: 'cards',
        content: JSON.stringify(content),
        parsedContent: content,
      });
    },
  },
};
</script>

<template>
  <a-modal
    :open="visible"
    :footer="null"
    width="1600px"
    @cancel="handleClose"
  >
    <div :class="b()">
      <div :class="b('title')">
        Choose What Type of <strong>{{ moduleTypeLabel }}</strong> You Want to Add
      </div>
      <div :class="b('separator')" />
      <div :class="b('templates')">
        <template v-if="isSelectTemplate && templates.length > 0">
          <div :class="b('select-module-wrapper')">
            <ModuleRenderer
              :module-data="getModuleData(templates[0])"
              :is-edit-mode="false"
              :is-popup="true"
              :company-id="companyId"
              :block-index="blockIndex"
              :company-colors="companyColors"
              @selection-change="handleSelectionChange"
            />
          </div>

          <template v-if="hasFirstBlock">
            <button
              :class="b('select-btn')"
              @click="handleAddFirstBlock"
            >
              ADD THIS SECTION
            </button>
            <div :class="b('separator')" />
          </template>

          <template v-if="hasSecondBlock">
            <div :class="b('select-module-wrapper')">
              <CollectionCardsModule
                :module-data="cardsModuleData"
                :is-popup="true"
                :company-id="companyId"
                :selected-ids="selectedIds"
                :item-type="itemType"
              />
            </div>
            <button
              :class="b('select-btn')"
              @click="handleAddSecondBlock"
            >
              ADD THIS SECTION
            </button>
          </template>
        </template>

        <template v-else>
          <div
            v-for="(template, index) in templates"
            :key="template.id"
            :class="b('template-card')"
          >
            <div :class="[b('preview'), getPreviewClass(template)]">
              <template v-if="hasRealComponent(template)">
                <ModuleRenderer
                  :module-data="getModuleData(template)"
                  :is-edit-mode="false"
                  :is-popup="true"
                />
              </template>

            <template v-else-if="template.preview?.layout === 'image-left'">
              <div :class="b('preview-image')" />
              <div :class="b('preview-content')">
                <div :class="b('preview-title')" />
                <div :class="b('preview-text')" />
                <div :class="b('preview-text', { short: true })" />
                <div v-if="template.preview?.hasButton" :class="b('preview-button')" />
              </div>
            </template>

            <template v-else-if="template.preview?.layout === 'image-right'">
              <div :class="b('preview-content')">
                <div :class="b('preview-title')" />
                <div :class="b('preview-text')" />
                <div :class="b('preview-text', { short: true })" />
                <div v-if="template.preview?.hasButton" :class="b('preview-button')" />
              </div>
              <div :class="b('preview-image')" />
            </template>

            <template v-else-if="template.preview?.layout === 'centered'">
              <div :class="b('preview-centered')">
                <div :class="b('preview-title', { centered: true })" />
                <div :class="b('preview-text', { centered: true })" />
                <div
                  v-if="template.preview?.hasButton"
                  :class="b('preview-button', { centered: true })"
                />
              </div>
            </template>

            <template v-else-if="template.preview?.layout === 'grid'">
              <div :class="b('preview-grid')">
                <div
                  v-for="i in (template.preview?.items || 4)"
                  :key="i"
                  :class="b('preview-grid-item')"
                />
              </div>
            </template>

            <template v-else-if="template.preview?.layout === 'list'">
              <div :class="b('preview-list')">
                <div
                  v-for="i in (template.preview?.items || 3)"
                  :key="i"
                  :class="b('preview-list-item')"
                >
                  <div :class="b('preview-list-image')" />
                  <div :class="b('preview-list-content')">
                    <div :class="b('preview-title', { small: true })" />
                    <div :class="b('preview-text', { small: true })" />
                  </div>
                </div>
              </div>
            </template>

            <template v-else-if="template.preview?.layout === 'featured'">
              <div :class="b('preview-featured')">
                <div :class="b('preview-featured-image')" />
                <div :class="b('preview-featured-info')">
                  <div :class="b('preview-title')" />
                  <div :class="b('preview-text')" />
                  <div :class="b('preview-button')" />
                </div>
              </div>
            </template>

            <template v-else-if="template.preview?.layout === 'slider'">
              <div :class="b('preview-slider')">
                <div :class="b('preview-slider-arrow', { left: true })">‹</div>
                <div :class="b('preview-slider-slides')">
                  <div
                    v-for="i in 3"
                    :key="i"
                    :class="b('preview-slider-slide', { active: i === 2 })"
                  />
                </div>
                <div :class="b('preview-slider-arrow', { right: true })">›</div>
              </div>
            </template>

            <template v-else-if="template.preview?.layout === 'masonry'">
              <div :class="b('preview-masonry')">
                <div :class="b('preview-masonry-col')">
                  <div :class="b('preview-masonry-item', { tall: true })" />
                  <div :class="b('preview-masonry-item')" />
                </div>
                <div :class="b('preview-masonry-col')">
                  <div :class="b('preview-masonry-item')" />
                  <div :class="b('preview-masonry-item', { tall: true })" />
                </div>
                <div :class="b('preview-masonry-col')">
                  <div :class="b('preview-masonry-item', { medium: true })" />
                  <div :class="b('preview-masonry-item')" />
                </div>
              </div>
            </template>

            <template v-else-if="template.preview?.layout === 'background-centered'">
              <div :class="b('preview-background')">
                <div :class="b('preview-background-box')">
                  <div :class="b('preview-title', { centered: true })" />
                  <div :class="b('preview-text', { centered: true })" />
                </div>
              </div>
            </template>

            <template v-else-if="template.preview?.layout === 'banner'">
              <div :class="b('preview-banner')">
                <div :class="b('preview-banner-overlay')">
                  <div :class="b('preview-title', { white: true })" />
                  <div :class="b('preview-button', { light: true })" />
                </div>
              </div>
            </template>

            <template v-else-if="template.preview?.layout === 'form'">
              <div :class="b('preview-form')">
                <div :class="b('preview-title', { centered: true })" />
                <div :class="b('preview-form-fields')">
                  <div :class="b('preview-form-input')" />
                  <div :class="b('preview-form-input')" />
                  <div :class="b('preview-form-textarea')" />
                </div>
                <div :class="b('preview-button', { centered: true })" />
              </div>
            </template>

            <template v-else>
              <div :class="b('preview-default')">
                <div :class="b('preview-icon')">
                  {{ template.name.charAt(0) }}
                </div>
              </div>
            </template>
          </div>

          <button
            :class="b('select-btn')"
            @click="handleSelect(template)"
          >
            ADD THIS SECTION
          </button>

          <div v-if="index < templates.length - 1" :class="b('separator')" />
          </div>
        </template>
      </div>
      <div v-if="templates.length === 0" :class="b('empty')">
        No templates available for this module type.
      </div>
    </div>
  </a-modal>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.module-select-modal {
  max-height: 80vh;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 8px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(8);
    }
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #5f8aa4;
    border-radius: 20px;

    @include mediaMinMax(1025, $normal) {
      border-radius: vw-full(20);
    }
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #4a7a94;
  }

  scrollbar-width: thin;
  scrollbar-color: #5f8aa4 transparent;

  &__title {
    font-family: Montserrat, sans-serif;
    font-size: 28px;
    font-weight: 500;
    color: #293241;
    text-align: center;
    margin-bottom: 0;

    strong {
      font-weight: 700;
    }

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(28);
    }
  }

  &__separator {
    width: 100%;
    height: 6px;
    background: #ddd;
    margin: 20px 0;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(6);
      margin: vw-full(20) 0;
    }

    &:last-child {
      display: none;
    }
  }

  &__templates {
    min-height: 600px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;

    @include mediaMinMax(1025, $normal) {
      min-height: vw-full(600);
    }
  }

  &__select-module-wrapper {
    width: 100%;
    max-width: 1200px;
    display: flex;
    flex-direction: column;
    align-items: center;

    @include mediaMinMax(1025, $normal) {
      max-width: vw-full(1200);
    }
  }

  &__template-card {
    width: 100%;
    max-width: 1544px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0;
    border: none;
    border-radius: 0;

    @include mediaMinMax(1025, $normal) {
      max-width: vw-full(1544);
    }
  }

  &__preview {
    width: 100%;
    padding: 0;
    margin-bottom: 24px;
    display: flex;
    gap: 0;
    overflow: hidden;
    justify-content: center;

    @include mediaMinMax(1025, $normal) {
      margin-bottom: vw-full(24);
    }

    &.create-wizard {
      height: auto;
      overflow: visible;

      @include mediaMinMax(1025, $normal) {
        height: auto;
      }
    }
  }

  &__select-btn {
    width: 400px;
    height: 52px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 20px;
    background: #FFF;
    box-shadow: 4px 4px 8px 0 rgba(36, 36, 36, 0.35);
    color: #242424;
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    font-weight: 500;
    text-transform: uppercase;
    border: 1px solid #242424;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 20px;

    &:hover {
      background: #242424;
      color: #fff;
    }

    @include mediaMinMax(1025, $normal) {
      width: vw-full(400);
      height: vw-full(52);
      font-size: vw-full(24);
      border-radius: vw-full(20);
      margin-bottom: vw-full(20);
    }
  }

  &__preview-image {
    width: 100px;
    height: 100%;
    background: #d9d9d9;
    border-radius: 4px;
    flex-shrink: 0;
  }

  &__preview-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__preview-title {
    height: 16px;
    width: 80%;
    background: #bfbfbf;
    border-radius: 2px;

    &_centered {
      margin: 0 auto;
      width: 60%;
    }

    &_small {
      height: 12px;
      width: 70%;
    }

    &_white {
      background: rgba(255, 255, 255, 0.9);
    }
  }

  &__preview-text {
    height: 10px;
    width: 100%;
    background: #e8e8e8;
    border-radius: 2px;

    &_short {
      width: 60%;
    }

    &_centered {
      margin: 0 auto;
      width: 80%;
    }

    &_small {
      height: 8px;
    }
  }

  &__preview-button {
    height: 28px;
    width: 80px;
    background: #242424;
    border-radius: 4px;
    margin-top: auto;

    &_centered {
      margin: 12px auto 0;
    }

    &_light {
      background: #fff;
    }
  }

  &__preview-centered {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  &__preview-grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  &__preview-grid-item {
    aspect-ratio: 1;
    background: #d9d9d9;
    border-radius: 4px;
  }

  &__preview-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__preview-list-item {
    display: flex;
    gap: 8px;
    padding: 6px;
    background: #fff;
    border-radius: 4px;
  }

  &__preview-list-image {
    width: 40px;
    height: 40px;
    background: #d9d9d9;
    border-radius: 4px;
    flex-shrink: 0;
  }

  &__preview-list-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    justify-content: center;
  }

  &__preview-featured {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__preview-featured-image {
    height: 80px;
    background: #d9d9d9;
    border-radius: 4px;
  }

  &__preview-featured-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__preview-slider {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__preview-slider-arrow {
    width: 24px;
    height: 24px;
    background: #e8e8e8;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8c8c8c;
    font-size: 16px;
    flex-shrink: 0;
  }

  &__preview-slider-slides {
    flex: 1;
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
  }

  &__preview-slider-slide {
    width: 60px;
    height: 80px;
    background: #d9d9d9;
    border-radius: 4px;
    opacity: 0.5;

    &_active {
      width: 80px;
      height: 100px;
      opacity: 1;
    }
  }

  &__preview-masonry {
    width: 100%;
    display: flex;
    gap: 6px;
  }

  &__preview-masonry-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__preview-masonry-item {
    height: 50px;
    background: #d9d9d9;
    border-radius: 4px;

    &_tall {
      height: 80px;
    }

    &_medium {
      height: 65px;
    }
  }

  &__preview-background {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #a8d8ea 0%, #aa96da 100%);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
  }

  &__preview-background-box {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 6px;
    padding: 16px;
    width: 80%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    &_cta {
      height: 100%;
      justify-content: space-between;
    }
  }

  &__preview-background-top {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  &__preview-banner {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 4px;
    position: relative;
  }

  &__preview-banner-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  &__preview-form {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  &__preview-form-fields {
    width: 80%;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__preview-form-input {
    height: 20px;
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 2px;
  }

  &__preview-form-textarea {
    height: 40px;
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 2px;
  }

  &__preview-default {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__preview-icon {
    width: 60px;
    height: 60px;
    background: #e8e8e8;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: 600;
    color: #595959;
  }

  &__empty {
    padding: 40px;
    text-align: center;
    color: #7f7f7f;
    font-size: 16px;
  }
}
</style>
